import styled, { useTheme } from 'styled-components'
import ReactSelect, {
  components,
  Props as RSProps,
  GroupBase,
  MultiValue,
  ActionMeta,
  OptionProps,
  MultiValueGenericProps,
} from 'react-select'
import React, { useMemo } from 'react'

import { ellipsizeTokenAddress } from 'utils/helpers'
import { IconSearch } from 'components/Icons/IconSearch'
import { Stylable } from 'utils/types'
import { useIsMobile } from 'utils/useIsMobile'
import { CoinOption, LeaderboardAutocompleteOption, OptionType } from 'modules/Leaderboard/utils'
import { IconWallet } from 'components/Icons/IconWallet'
import { IconPosition } from 'components/Icons/IconPosition'
import { Typography } from 'components/Typography/Typography'

export interface AutocompleteProps extends Stylable {
  options: LeaderboardAutocompleteOption[]
  value: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}

const DropdownIndicator = (props: any) => {
  const isMobile = useIsMobile()

  return (
    <components.DropdownIndicator {...props}>
      <IconSearch width={isMobile ? 16 : 24} height={isMobile ? 16 : 24} />
    </components.DropdownIndicator>
  )
}

type RSAutocompleteMultiProps = RSProps<LeaderboardAutocompleteOption, true, GroupBase<LeaderboardAutocompleteOption>>

const BaseAutocompleteMultiSelect = (props: RSAutocompleteMultiProps) => <ReactSelect {...props} />

const StyledSelect = styled(BaseAutocompleteMultiSelect)`
  width: 500px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }

  .react-select__placeholder {
    color: ${({ theme }) => theme.colors.text.secondary} !important;
    font-size: ${({ theme }) => theme.typography.size.Description}px;
    text-align: left;
  }

  .react-select__control {
    border-radius: 999px;
    padding: 2px 10px;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.text.primary};
    cursor: text;
    &:hover {
      border: 1px solid ${({ theme }) => theme.colors.text.primary}; /* 👈 keep same */
    }
  }

  .react-select__control--is-focused {
    box-shadow: none !important;
    border-color: ${({ theme }) => theme.colors.text.primary} !important;
  }

  .react-select__indicator-separator {
    display: none;
  }
  .react-select__dropdown-indicator {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .react-select__menu {
    background: ${({ theme }) => theme.colors.background.primary};
    border: 1px solid ${({ theme }) => theme.colors.text.primary};
    z-index: 3;
  }
  .react-select__option {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.size.Description}px;
    @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
      font-size: ${p => p.theme.typography.size.Caption1}px;
    }
  }
  .react-select__option--is-focused {
    background: ${({ theme }) => theme.colors.background.hover};
  }
  .react-select__option--is-selected {
    background: ${({ theme }) => theme.colors.background.hover};
  }

  .react-select__multi-value {
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.text.primary};
    border-radius: 999px;
    font-size: ${({ theme }) => theme.typography.size.Description}px;
  }
  .react-select__multi-value__label {
    color: ${({ theme }) => theme.colors.text.primary};
    padding: 2px 8px;
  }
  .react-select__multi-value__remove {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.primary};
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;

    &:hover {
      background: transparent;
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }

  .react-select__single-value,
  .react-select__placeholder,
  .react-select__input-container {
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.size.Description}px;

    @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
      font-size: ${p => p.theme.typography.size.Caption1}px;
    }
  }

  .react-select__menu,
  .react-select__option {
    -webkit-tap-highlight-color: transparent;
    user-select: none;
  }

  .react-select__option:active,
  .react-select__option--is-focused:active,
  .react-select__option--is-selected:active {
    background: ${({ theme }) => theme.colors.background.hover};
  }

  .react-select__value-container {
    height: 100%;
    padding: 0 6px;
  }

  .react-select__input-container {
    margin: 0;
    padding: 0;
  }

  .react-select__indicators {
    height: 100%;
  }
`

const Row = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  img {
    margin-right: 18px;
  }
  svg {
    margin-right: 18px;
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`
const RowImg = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  grid-row: 1 / span 2;
  object-fit: cover;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 24px;
    height: 24px;
  }
`
const RowTitle = styled(Typography)`
  font-weight: 600;
  line-height: 1.1;
  text-align: left;
`
const RowDesc = styled(Typography)`
  line-height: 1.1;
`

const Chip = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }

  svg {
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }
`

function getVisual(opt: LeaderboardAutocompleteOption): {
  icon: React.ReactNode
  title: string
  desc: string
  type: OptionType
} {
  switch (opt.type) {
    case 'coin':
      return {
        icon: (opt as CoinOption).iconUrl ? (
          <RowImg as="img" src={(opt as CoinOption).iconUrl} alt={(opt as CoinOption).displaySymbol} />
        ) : (
          <RowImg as="div" />
        ),
        title: (opt as CoinOption).displaySymbol,
        desc: (opt as CoinOption).name,
        type: opt.type,
      }
    case 'wallet':
      return {
        icon: <IconWallet />,
        title: 'Wallet',
        desc: opt.id,
        type: opt.type,
      }
    case 'position':
      return {
        icon: <IconPosition />,
        title: 'Position ID',
        desc: opt.id,
        type: opt.type,
      }
    default:
      return { icon: <div />, title: 'Unknown', desc: '', type: opt.type }
  }
}

const CustomOption = (
  props: OptionProps<LeaderboardAutocompleteOption, true, GroupBase<LeaderboardAutocompleteOption>>
) => {
  const { data } = props
  const v = getVisual(data)
  const isMobile = useIsMobile()

  return (
    <components.Option {...props}>
      <Row>
        {v.icon}
        <div>
          <RowTitle variant={isMobile ? 'body' : 'label2'}>{v.title}</RowTitle>
          <RowDesc variant={isMobile ? 'caption' : 'body'} color="secondary">
            {v.type === 'coin' ? v.desc : ellipsizeTokenAddress(v.desc)}
          </RowDesc>
        </div>
      </Row>
    </components.Option>
  )
}

const MultiValueLabel = (props: MultiValueGenericProps<LeaderboardAutocompleteOption>) => {
  const { data } = props
  const v = getVisual(data)

  return (
    <components.MultiValueLabel {...props}>
      <Chip style={{ display: 'flex', alignItems: 'center' }}>
        {React.isValidElement(v.icon)
          ? React.cloneElement(v.icon as React.ReactElement<{ width?: number; height?: number }>, {
              width: 16,
              height: 16,
            })
          : v.icon}
        <span>{v.type === 'coin' ? v.title : ellipsizeTokenAddress(v.desc)}</span>
      </Chip>
    </components.MultiValueLabel>
  )
}

export const EntityAutocomplete = ({
  options,
  value,
  onChange,
  placeholder = 'Search by wallet',
}: AutocompleteProps) => {
  const theme = useTheme()
  const isMobile = useIsMobile()

  const byId = new Map(options.map(o => [o.id, o]))
  const selected = value.map(id => byId.get(id)).filter(Boolean) as LeaderboardAutocompleteOption[]

  const optionsToUse = useMemo(() => {
    const selectedCoins = new Set(selected.filter(s => s.type === 'coin').map(s => s.id))
    const selectedWalletId = selected.find(s => s.type === 'wallet')?.id
    const selectedPositionId = selected.find(s => s.type === 'position')?.id
    const pairSelected = selectedCoins.size === 2

    const optionsToUse = options.filter(o => {
      switch (o.type) {
        case 'coin':
          return !pairSelected || selectedCoins.has(o.id)

        case 'wallet':
          return !selectedWalletId || o.id === selectedWalletId

        case 'position':
          return !selectedPositionId || o.id === selectedPositionId
        default:
          return true
      }
    })
    return optionsToUse
  }, [selected, options])

  const handleChange = (
    newValue: MultiValue<LeaderboardAutocompleteOption>,
    _meta: ActionMeta<LeaderboardAutocompleteOption>
  ) => {
    onChange(newValue.map(o => o.id))
  }

  return (
    <StyledSelect
      classNamePrefix="react-select"
      isMulti
      isSearchable
      isClearable={false}
      closeMenuOnSelect={true}
      hideSelectedOptions={false}
      backspaceRemovesValue
      options={optionsToUse}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      getOptionValue={o => o.id}
      components={{ DropdownIndicator, IndicatorSeparator: () => null, Option: CustomOption, MultiValueLabel }}
      styles={{
        option: base => ({
          ...base,
          paddingTop: isMobile ? 8 : 12,
          paddingBottom: isMobile ? 8 : 12,
          borderBottom: `1px solid ${theme.colors.divider}`,
          ':last-child': {
            borderBottom: 'none',
          },
        }),
      }}
      filterOption={(candidate, input) => {
        const o = candidate.data as LeaderboardAutocompleteOption
        const v = getVisual(o)
        const q = input.toLowerCase().trim()
        if (!q) {
          return o.type === 'coin'
        }
        if (o.type === 'coin') {
          return o.id.toLowerCase().includes(q) || v.title.toLowerCase().includes(q) || v.desc.toLowerCase().includes(q)
        }
        return o.id === q
      }}
    />
  )
}
